import type { ReactNode } from 'react';
import { ChevronDown, Mail, MessageSquare, Phone } from 'lucide-react';

import { Button } from '@/components/atoms/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';

/**
 * The single-scope sticky action bar, shared by the public quote and the portal
 * estimate detail. It pins the live running total (which moves as the customer
 * toggles optional add-ons) beside the primary "Approve & schedule" action and
 * an optional "Ask" contact menu for the business. An optional scope `eyebrow` and a
 * financing `subline` sit around the total, matching the target document.
 *
 * Presentational + wiring-agnostic: each surface passes its own `onPrimary`
 * (scroll to the approve flow on the public page, open the approve step in the
 * portal) and its own `fmt`. `ask` contains the available business contact channels.
 */
export function SingleScopeActionBar({
  eyebrow,
  total,
  subline,
  fmt,
  primaryLabel,
  onPrimary,
  primaryDisabled = false,
  ask,
}: {
  /** Quiet scope label above the total, e.g. "LIKE-FOR-LIKE REPLACEMENT + 1 ADD-ON". */
  eyebrow?: ReactNode;
  total: number;
  /** Financing hint under the total, e.g. "or about $135/mo for 24 months". */
  subline?: ReactNode;
  fmt: (amount: number) => string;
  primaryLabel?: string;
  onPrimary?: () => void;
  primaryDisabled?: boolean;
  /** Available contact channels; the menu hides when neither is provided. */
  ask?: { phone?: string | null; email?: string | null } | null;
}) {
  const dialNumber = ask?.phone?.trim().replace(/[^\d+]/g, '') ?? '';
  const hasPhone = /\d/.test(dialNumber);
  const email = ask?.email?.trim();

  return (
    // A floating rounded pill (the artifact's .abar), not a full-width bar:
    // centered at the document measure, bordered, shadowed and blurred.
    <div className="pointer-events-none sticky bottom-0 z-20 px-4 pb-4">
      {/* On a phone: total and Ask share the top row, the primary action takes
          the full width beneath, so a long "Pay $X deposit to approve" never
          sits on the total. From sm up it is one row. */}
      <div className="pointer-events-auto mx-auto grid max-w-[760px] grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-3 rounded-[10px] border border-[#14141A]/[0.13] bg-white/[0.94] px-5 py-3.5 shadow-[0_12px_34px_rgba(10,10,10,0.11),0_3px_8px_rgba(10,10,10,0.05)] backdrop-blur-[14px] sm:flex sm:justify-between sm:gap-[15px]">
        <div className="min-w-0">
          {eyebrow ? (
            <p className="truncate text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[#95969D]">
              {eyebrow}
            </p>
          ) : null}
          <p className="mt-0.5 text-[23px] font-medium tracking-[-0.03em] tabular-nums text-[#17171A]">
            {fmt(total)}
          </p>
          {subline ? <p className="mt-0.5 text-[12.5px] text-[#63646B]">{subline}</p> : null}
        </div>
        <div className="contents sm:flex sm:shrink-0 sm:items-center sm:gap-2">
          {hasPhone || email ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="justify-self-end">
                  Ask <ChevronDown className="ml-1 size-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="end">
                {hasPhone ? (
                  <>
                    <DropdownMenuItem asChild>
                      <a href={`sms:${dialNumber}`}>
                        <MessageSquare /> Send a text
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <a href={`tel:${dialNumber}`}>
                        <Phone /> Call
                      </a>
                    </DropdownMenuItem>
                  </>
                ) : null}
                {email ? (
                  <DropdownMenuItem asChild>
                    <a href={`mailto:${email}`}>
                      <Mail /> Send an email
                    </a>
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
          {primaryLabel ? (
            <Button
              size="sm"
              onClick={onPrimary}
              disabled={primaryDisabled}
              className="col-span-2 h-10 w-full bg-[var(--ss-accent)] text-white hover:bg-[var(--ss-accent-strong)] sm:h-8 sm:w-auto"
            >
              {primaryLabel}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
